import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import { ArrowLeft, DollarSign, FileText, Users } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { fmtCurrency } from '../../lib/display-labels';
import { tenantTodayDate } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/** Whole-dollar money. @see fmtCurrency — absence renders '—', not '$0'. */
const fmt = (n: number | string | null | undefined) => fmtCurrency(n, null, 0);

export default function RevenueSummaryReport() {
  const navigate = useNavigate();
  // The TENANT's year and month (companies.time_zone), never the device's. No
  // zone, no year: the query waits and no month is marked current.
  const timeZone = useTenantTimeZone();
  const tenantToday = tenantTodayDate(timeZone);
  const currentYear = tenantToday ? tenantToday.getFullYear() : null;
  const [year, setYear] = useState<number | null>(currentYear);

  const { data, isLoading } = useQuery({
    queryKey: ['revenue-summary', year],
    queryFn: () => apiClient.get('/surface-rentals/reports/revenue-summary', { params: { year } }).then(r => unwrap<any>(r.data)),
    enabled: year !== null,
  });

  const months: any[] = useMemo(() => {
    if (!data?.months) return [];
    return data.months;
  }, [data]);

  const chartData = useMemo(() => {
    return MONTHS.map((label, i) => {
      const m = months.find((x: any) => x.month === i + 1);
      return { name: label, revenue: m?.revenue || 0 };
    });
  }, [months]);

  // GET /rentals/reports/revenue-summary returns { year, months, totals:
  // { revenue, invoices, customers } }. The old code read data.total_revenue /
  // .total_invoices / .total_customers, none of which the API has ever sent.
  // Revenue and invoices happened to survive on their month-sum fallback;
  // customers had none, so "Total Customers" read 0 for every tenant — and the
  // months cannot be summed for it, because the API's count is DISTINCT across
  // the year (a customer who rents in March and April is one customer, not two).
  const totals = data?.totals;
  const totalRevenue = totals?.revenue ?? months.reduce((s: number, m: any) => s + (m.revenue || 0), 0);
  const totalInvoices = totals?.invoices ?? months.reduce((s: number, m: any) => s + (m.invoices || 0), 0);
  const totalCustomers = totals?.customers ?? 0;
  const currentMonth = tenantToday ? tenantToday.getMonth() : null;

  const isEmpty = !isLoading && months.length === 0;

  return (
    <div className="min-h-full pb-[var(--bottom-chrome)] bg-background">
      
      <div className="border-b border-border px-6 py-4">
        <button onClick={() => navigate('/reports')} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <h1 className="text-base font-bold text-foreground">Revenue Summary</h1>
          <div className="flex items-center gap-3">
            <select
              value={year ?? ''}
              onChange={e => setYear(Number(e.target.value))}
              className="bg-fieldiq-card border border-fieldiq-border rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-fieldiq-accent"
            >
              {(currentYear === null ? [] : [currentYear, currentYear - 1, currentYear - 2]).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 pt-4 space-y-4">
        {/* KPI cards */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-3 text-center">
            <DollarSign className="w-4 h-4 text-fieldiq-accent mx-auto mb-1" />
            <p className="text-lg font-bold text-fieldiq-accent">{fmt(totalRevenue)}</p>
            <p className="text-[10px] text-fieldiq-muted">Total Revenue</p>
          </div>
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-3 text-center">
            <FileText className="w-4 h-4 text-fieldiq-accent mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{totalInvoices}</p>
            <p className="text-[10px] text-fieldiq-muted">Total Invoices</p>
          </div>
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-3 text-center">
            <Users className="w-4 h-4 text-fieldiq-accent mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{totalCustomers}</p>
            <p className="text-[10px] text-fieldiq-muted">Total Customers</p>
          </div>
        </div>

        {isEmpty ? (
          <div className="text-center py-16">
            <DollarSign size={40} className="mx-auto text-fieldiq-muted mb-3" />
            <p className="text-fieldiq-muted text-sm">No invoice data for {year}. Create rental orders and generate invoices to see revenue here.</p>
          </div>
        ) : (
          <>
            {/* Bar chart */}
            <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-4">
              <h3 className="text-sm font-semibold text-white mb-3">Monthly Revenue</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--secondary))" />
                  <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'hsl(var(--secondary))', border: '1px solid hsl(var(--border))', borderRadius: 8 }}
                    labelStyle={{ color: '#fff' }}
                    formatter={(value: any) => [fmt(Number(value || 0)), 'Revenue']}
                  />
                  <Bar dataKey="revenue" fill="#d97706" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Table */}
            <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-fieldiq-border text-fieldiq-muted text-xs">
                      <th className="text-left px-4 py-2.5 font-medium">Month</th>
                      <th className="text-right px-4 py-2.5 font-medium">Revenue</th>
                      <th className="text-right px-4 py-2.5 font-medium">Invoices</th>
                      <th className="text-right px-4 py-2.5 font-medium">Customers</th>
                      <th className="text-right px-4 py-2.5 font-medium">vs Prior</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MONTHS.map((label, i) => {
                      const m = months.find((x: any) => x.month === i + 1);
                      const prev = months.find((x: any) => x.month === i);
                      const rev = m?.revenue || 0;
                      const prevRev = prev?.revenue || 0;
                      const pctChange = prevRev > 0 ? ((rev - prevRev) / prevRev * 100) : 0;
                      const isCurrent = year === currentYear && i === currentMonth;

                      return (
                        <tr key={i} className={`border-b border-fieldiq-border/50 ${isCurrent ? 'bg-fieldiq-accent/10' : ''}`}>
                          <td className="px-4 py-2.5 text-white font-medium">{label}{isCurrent && <span className="ml-1 text-[10px] text-fieldiq-accent">(current)</span>}</td>
                          <td className="px-4 py-2.5 text-right text-white">{fmt(rev)}</td>
                          <td className="px-4 py-2.5 text-right text-fieldiq-muted">{m?.invoices || 0}</td>
                          <td className="px-4 py-2.5 text-right text-fieldiq-muted">{m?.customers || 0}</td>
                          <td className={`px-4 py-2.5 text-right font-medium ${pctChange > 0 ? 'text-green-400' : pctChange < 0 ? 'text-red-400' : 'text-fieldiq-muted'}`}>
                            {i === 0 ? '—' : `${pctChange >= 0 ? '+' : ''}${pctChange.toFixed(1)}%`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {isLoading && (
          <div className="text-center py-16">
            <div className="w-8 h-8 border-2 border-fieldiq-accent border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        )}
      </div>
    </div>
  );
}
